import {Display, Heading, Icon, Show, Text} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import styled from 'styled-components';

import {isNotNil} from 'ramda';

import type {AdvertisementPreviewPlatform} from '@dms/api/sales';
import type {GetVehicleApiResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {getPreviewContainerBreakpoint} from './getPreviewContainerBreakpoint';
import {PREVIEW_CONTAINER_BREAKPOINTS} from './PREVIEW_CONTAINER_BREAKPOINTS';

export function AdvertisementPreviewPrice(props: {
  selectedPlatform?: AdvertisementPreviewPlatform;
  pricingVehicle?: GetVehicleApiResponse;
}) {
  const formatCurrency = useFormatCurrency();
  const price = props.selectedPlatform?.price;
  const withVat = isNotNil(props.selectedPlatform)
    ? price
    : (props.pricingVehicle?.sellingPrice?.withVat ?? null);
  const withoutVat = isNotNil(props.selectedPlatform)
    ? (price?.withoutVat ?? null)
    : (props.pricingVehicle?.sellingPrice?.withoutVat ?? null);
  const isVatDeductible = isNotNil(props.selectedPlatform)
    ? price?.isVatDeductible
    : props.pricingVehicle?.isVatDeductible;
  const vatLabel =
    isNotNil(withVat) && typeof isVatDeductible === 'boolean'
      ? i18n.t(
          isVatDeductible
            ? 'general.labels.vatDeductible'
            : 'entity.vehicle.labels.withoutVatDeductible'
        )
      : null;

  return (
    <PricePanel>
      <Text size="xSmall" color="white">
        {i18n.t('general.labels.withVat')}
      </Text>
      <PriceValue>
        <Display size={1} color="white" headingLevel={3}>
          {isNotNil(withVat)
            ? formatCurrency(Number(withVat.amount), withVat.currency)
            : i18n.t('page.advertisement.preview.priceNotSet')}
        </Display>
      </PriceValue>
      <Show when={isNotNil(withoutVat) || isNotNil(vatLabel)}>
        <NetPrice>
          <Text size="xSmall" color="white">
            {i18n.t('general.labels.withoutVat')}
          </Text>
          <NetPriceDetails>
            <Show when={withoutVat}>
              {(withoutVat) => (
                <PriceValue>
                  <Heading size={2} color="white" headingLevel={4}>
                    {formatCurrency(Number(withoutVat.amount), withoutVat.currency)}
                  </Heading>
                </PriceValue>
              )}
            </Show>
            <Show when={vatLabel}>
              {(vatLabel) => (
                <VatBadge $deductible={isVatDeductible === true}>
                  <Show when={isVatDeductible === true}>
                    <Icon value="navigation/check" size={4} color="severity.success" />
                  </Show>
                  {vatLabel}
                </VatBadge>
              )}
            </Show>
          </NetPriceDetails>
        </NetPrice>
      </Show>
    </PricePanel>
  );
}

const PricePanel = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(4)};
  padding: ${({theme}) => theme.getSize(5)};
  border-radius: ${({theme}) => theme.radii.medium};
  color: ${({theme}) => theme.colors.text.white};
  background: ${({theme}) => theme.colors.palettes.neutral[900][100]};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.wide
  )}) {
    padding: ${({theme}) => theme.getSize(4)};
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.compact
  )}) {
    gap: ${({theme}) => theme.getSize(3)};
  }
`;
const PriceValue = styled.div`
  overflow-wrap: anywhere;
`;
const NetPrice = styled.div`
  display: grid;
  gap: ${({theme}) => theme.getSize(1)};
  padding-top: ${({theme}) => theme.getSize(3)};
  border-top: 1px solid ${({theme}) => theme.colors.general.separator};
`;
const NetPriceDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({theme}) => theme.getSize(2)};
`;
const VatBadge = styled.span<{$deductible: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.circular};
  color: ${({theme, $deductible}) =>
    $deductible ? theme.colors.severity.success : theme.colors.text.primary};
  background: ${({theme, $deductible}) =>
    $deductible ? theme.colors.palettes.green[10][100] : theme.colors.palettes.white[10][100]};
`;
